/**
 * One business account, as a dashboard rather than a stack of forms.
 *
 * Owner, Oct 19 2026: "I want the commercial bidding and profile side to be
 * way cooler." What he opens this page for, in order: who they are and where
 * they stand (the header and the numbers), their sites on a map with a bid
 * and a site map one tap away, what has happened and what is coming (the
 * feed). The billing form, AP contacts, papers and bills still live here,
 * folded under one line each, open on a tap.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import {
  ArrowLeft, ExternalLink, Plus, Edit2, Trash2, Send, FileText, Upload, AlertTriangle, Map as MapIcon,
  ChevronDown, Camera, Receipt, Calendar, CheckCircle2, Clock, Sparkles, Building2, ShieldCheck, Mail, Link as LinkIcon,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import { useReadOnly } from '../hooks/useReadOnly';
import { money } from '../utils/format';
import { fmtTs, businessToday, parseDbTs, BUSINESS_TZ } from '../utils/time';
import {
  TERMS_OPTIONS, termsLabel, absUrl, dayLabel, DOC_KINDS, docKindLabel, expiryTone, daysUntil,
} from '../utils/commercial';
import { Field, pricingLabel, initials } from './CommercialAccounts';
import ProposalPaste from '../components/ProposalPaste';
import AccountMark from '../components/AccountMark';

type Id = number;
interface Site {
  id: Id; label: string | null; address: string; city: string | null; zip_code: string | null;
  lat: number | null; lng: number | null; mowing_price: number | null; estimated_minutes: number | null;
  gate_notes: string | null; access_notes: string | null; crew_notes: string | null; service_status: string;
  site_contact_name: string | null; site_contact_phone: string | null; site_contact_email: string | null;
  next_visit: string | null; last_visit: string | null; frequency: string | null; route_name: string | null;
  map_areas?: number; map_locks?: number;   // what is drawn on the site map — counts only
}
interface Doc {
  id: Id; kind: 'coi' | 'w9' | 'contract' | 'other'; title: string | null; mime_type: string | null;
  size_bytes: number | null; expires_on: string | null; notes: string | null; uploaded_at: string | null; url: string | null;
}
interface Report {
  id: Id; job_id: Id; status: 'pending' | 'sent' | 'failed'; sent_to: string | null; sent_at: string | null;
  error: string | null; note: string | null; created_at: string | null; date: string | null; site: string | null;
  photos: number; url: string | null;
}
interface Unreported { job_id: Id; date: string | null; site: string | null; photos: number }
interface Invoice {
  id: Id; invoice_number: string; status: string; total: number; due_date: string | null; po_number: string | null;
  billing_period_start: string | null; billing_period_end: string | null; created_at: string | null;
  paid_at: string | null; paid: number;
}
interface Contact {
  id: Id; property_id: Id | null; name: string | null; role: string | null; phone: string | null; email: string | null; address: string | null;
  gets_bills: boolean; gets_reports: boolean; notes: string | null;
}
interface Visit {
  id: Id; date: string; time_start: string | null; status: string; title: string | null; service_type: string;
  price: number | null; property_id: Id | null; site: string | null; crew: string | null; photos: number; report: string | null;
}
interface Detail {
  account: any; sites: Site[]; contacts?: Contact[]; documents: Doc[]; reports: Report[]; unreported: Unreported[];
  invoices: Invoice[]; contract_bills: { month: string; invoice_id: Id | null; amount: number }[];
  quotes?: any[]; visits?: Visit[]; value?: { month: number; schedules: number; estimated: boolean };
}

const errToast = (fallback: string) => (e: any) => {
  if (!e?.isReadOnlyBlock) toast.error(e?.response?.data?.message || fallback);
};
const day = (v: any) => (v ? String(v).slice(0, 10) : '');
// A timestamp's day in Austin: a bid made at 9 PM is today's, not tomorrow's (the column is UTC).
const localDay = (ts: any): string => { const d = parseDbTs(ts); return d ? d.toLocaleDateString('en-CA', { timeZone: BUSINESS_TZ }) : ''; };
const shortDay = (d?: string | null) => (d ? new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—');
const clock = (t?: string | null) => { if (!t) return ''; const [h, m] = String(t).split(':').map(Number); return `${h % 12 || 12}:${String(m || 0).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`; };

/**
 * A card with a heading. With `summary` it starts folded: one line that says
 * what is in it, open on a tap — the forms stop crowding the page.
 */
function Section({ title, action, children, summary, icon, defaultOpen = false }: {
  title: string; action?: React.ReactNode; children: React.ReactNode;
  summary?: React.ReactNode; icon?: React.ReactNode; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (summary === undefined) {
    return (
      <section className="card p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h2 className="text-base font-black uppercase tracking-tight text-gray-900 inline-flex items-center gap-2">{icon}{title}</h2>
          {action}
        </div>
        {children}
      </section>
    );
  }
  return (
    <section className="card overflow-hidden">
      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 sm:px-5 py-3.5 text-left hover:bg-gray-50 transition">
        <span className="h-9 w-9 rounded-2xl bg-brand-50 text-brand-700 flex items-center justify-center flex-shrink-0">{icon}</span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-black uppercase tracking-tight text-gray-900">{title}</span>
          <span className="block text-sm text-gray-600 truncate">{summary}</span>
        </span>
        <ChevronDown className={`h-5 w-5 text-gray-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-gray-100 pt-4">
          {action && <div className="flex justify-end mb-3">{action}</div>}
          {children}
        </div>
      )}
    </section>
  );
}

export default function CommercialAccount() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const readOnly = useReadOnly();
  const [paste, setPaste] = useState(false);
  const { data, isLoading, isError } = useQuery(['commercial', id], () =>
    api.get(`/commercial/${id}`).then(r => r.data as Detail), { enabled: !!id });

  if (isLoading) return <div className="card p-8 text-center text-gray-500">Loading…</div>;
  if (isError || !data?.account) {
    return (
      <div className="card p-8 text-center">
        <p className="font-semibold text-gray-900">Account not found.</p>
        <Link to="/commercial" className="text-brand-700 font-bold underline text-sm">Back to accounts</Link>
      </div>
    );
  }

  const a = data.account;
  const company = a.company_name || a.name;
  const sites = data.sites || [];
  const activeSites = sites.filter(s => s.service_status !== 'cancelled');
  const newBid = (propertyId?: Id) => navigate('/quotes', { state: { prefillCustomerId: a.id, prefillPropertyId: propertyId ?? (activeSites.length === 1 ? activeSites[0].id : undefined) } });

  return (
    <div className="space-y-4">
      <Link to="/commercial" className="inline-flex items-center gap-1 text-sm font-semibold text-gray-600 hover:text-gray-900">
        <ArrowLeft className="h-4 w-4" /> Accounts
      </Link>

      <Hero account={a} company={company} documents={data.documents} invoices={data.invoices}
        onNewBid={() => newBid()} onPaste={() => setPaste(true)} readOnly={readOnly} />
      <Numbers account={a} sites={activeSites} visits={data.visits || []} reports={data.reports} unreported={data.unreported} invoices={data.invoices} value={data.value} />
      <SitesCard accountId={a.id} sites={sites} quotes={data?.quotes || []} onNewBid={newBid} />
      <ActivityCard visits={data.visits || []} reports={data.reports} invoices={data.invoices} quotes={data?.quotes || []} />

      <BillingCard account={a} />
      <ContactsCard accountId={a.id} contacts={data.contacts || []} />
      <ReportsCard account={a} reports={data.reports} unreported={data.unreported} />
      <DocumentsCard accountId={a.id} documents={data.documents} />
      <InvoicesCard account={a} invoices={data.invoices} contractBills={data.contract_bills} />
      <RemoveCard accountId={a.id} company={company} />

      {paste && <ProposalPaste onClose={() => setPaste(false)} />}
    </div>
  );
}

// ── Header ───────────────────────────────────────────────────────────────────

function Pill({ tone = 'gray', children }: { tone?: 'gray' | 'good' | 'warn' | 'bad' | 'brand'; children: React.ReactNode }) {
  const skin = tone === 'good' ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : tone === 'warn' ? 'bg-amber-50 text-amber-800 border-amber-300'
    : tone === 'bad' ? 'bg-red-50 text-red-800 border-red-200'
    : tone === 'brand' ? 'bg-brand-50 text-brand-800 border-brand-200'
    : 'bg-gray-50 text-gray-700 border-gray-200';
  return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${skin}`}>{children}</span>;
}

function Hero({ account: a, company, documents, invoices, onNewBid, onPaste, readOnly }: {
  account: any; company: string; documents: Doc[]; invoices: Invoice[]; onNewBid: () => void; onPaste: () => void; readOnly: boolean;
}) {
  const contact = [a.name !== company ? a.name : null, a.email, a.phone].filter(Boolean).join(' · ');
  const coi = documents.filter(d => d.kind === 'coi').map(d => d.expires_on).filter(Boolean).sort().pop() || null;
  const coiDays = daysUntil(coi);
  const hasCoi = documents.some(d => d.kind === 'coi');
  const endDays = daysUntil(a.contract_end);
  const pastDue = invoices.filter(i => ['sent', 'overdue'].includes(i.status) && Number(i.total) - Number(i.paid || 0) > 0 && (daysUntil(i.due_date) ?? 0) < 0).length;
  const reportsOn = a.service_reports_on == null ? true : !!Number(a.service_reports_on);
  // Their own page (services/businessPortal): sites, visits with reports, bills, papers. Copy the link to text or email it.
  const copyPortal = async () => {
    try {
      const url = (await api.get(`/customers/${a.id}/portal`)).data?.url;
      if (!url) throw new Error('no link');
      await navigator.clipboard?.writeText(url);
      toast.success('Their page link is copied — text or email it to them');
    } catch { toast.error('Could not get their page link'); }
  };
  return (
    <header className="card overflow-hidden">
      <div className="h-1.5 bg-gradient-to-r from-brand-600 via-brand-400 to-brand-600" />
      <div className="p-4 sm:p-5 flex flex-wrap items-start gap-4">
        <div className="h-16 w-16 rounded-3xl bg-gray-900 text-white flex items-center justify-center text-2xl font-black tracking-tight flex-shrink-0 shadow-soft">
          {initials(company)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-black uppercase tracking-wide text-brand-700">Business account</p>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900 break-words leading-tight">{company}<AccountMark mark={a.account_mark} /></h1>
          <p className="text-sm text-gray-600 break-words mt-0.5">{contact || 'No main contact yet'}</p>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            <Pill tone="brand">{pricingLabel(a)}</Pill>
            <Pill>{termsLabel(a.payment_terms)}{a.default_po_number ? ` · PO ${a.default_po_number}` : ''}</Pill>
            {a.contract_end && (endDays != null) && (
              <Pill tone={endDays < 0 ? 'bad' : endDays <= 30 && !Number(a.contract_auto_renew) ? 'warn' : 'gray'}>
                <Calendar className="h-3 w-3" />
                {endDays < 0 ? `Contract ended ${dayLabel(a.contract_end)}` : `Contract to ${dayLabel(a.contract_end)}${Number(a.contract_auto_renew) ? ' · renews' : ''}`}
              </Pill>
            )}
            {hasCoi
              ? <Pill tone={coiDays == null ? 'good' : coiDays < 0 ? 'bad' : coiDays <= 30 ? 'warn' : 'good'}>
                  <ShieldCheck className="h-3 w-3" />
                  {coiDays == null ? 'COI on file' : coiDays < 0 ? `COI expired ${dayLabel(coi)}` : coiDays <= 30 ? `COI expires in ${coiDays}d` : `COI to ${dayLabel(coi)}`}
                </Pill>
              : <Pill tone="warn"><ShieldCheck className="h-3 w-3" /> No COI</Pill>}
            <Pill tone={reportsOn ? 'good' : 'gray'}><Camera className="h-3 w-3" /> {reportsOn ? 'Photo report after every visit' : 'Reports off'}</Pill>
            {pastDue > 0 && <Pill tone="bad"><AlertTriangle className="h-3 w-3" /> {pastDue} bill{pastDue === 1 ? '' : 's'} past due</Pill>}
          </div>
        </div>
        <div className="flex sm:flex-col gap-2 w-full sm:w-auto">
          <button onClick={onNewBid} disabled={readOnly} className="btn-primary flex-1 sm:flex-none"><Sparkles className="h-4 w-4" /> New bid</button>
          <button onClick={onPaste} disabled={readOnly} className="btn-secondary flex-1 sm:flex-none">Paste a proposal</button>
          <div className="hidden sm:flex items-center justify-end gap-3">
            <button type="button" onClick={copyPortal} className="inline-flex items-center gap-1 text-xs font-bold text-brand-700 underline">
              <LinkIcon className="h-3 w-3" /> Their page link
            </button>
            <Link to={`/customers/${a.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-brand-700 underline">
              Customer record <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}

// ── The numbers ──────────────────────────────────────────────────────────────
function Stat({ label, value, sub, tone = 'gray', icon }: { label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: 'gray' | 'good' | 'warn' | 'bad' | 'brand'; icon?: React.ReactNode }) {
  const subSkin = tone === 'bad' ? 'text-red-700 font-bold' : tone === 'warn' ? 'text-amber-700 font-bold' : tone === 'good' ? 'text-emerald-700 font-semibold' : 'text-gray-600';
  return (
    <div className="card p-3.5 min-w-0">
      <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 inline-flex items-center gap-1">{icon}{label}</p>
      <p className="text-xl font-black text-gray-900 tabular-nums truncate mt-0.5">{value}</p>
      {sub && <p className={`text-xs truncate ${subSkin}`}>{sub}</p>}
    </div>
  );
}

function Numbers({ account, sites, visits, reports, unreported, invoices, value }: {
  account: any; sites: Site[]; visits: Visit[]; reports: Report[]; unreported: Unreported[]; invoices: Invoice[]; value?: Detail['value'];
}) {
  const today = businessToday();
  const month = today.slice(0, 7);
  const done = visits.filter(v => v.status === 'complete' && v.date.startsWith(month)).length;
  const booked = visits.filter(v => ['scheduled', 'in_progress'].includes(v.status) && v.date.startsWith(month) && v.date >= today).length;
  const next = visits.filter(v => ['scheduled', 'in_progress'].includes(v.status) && v.date >= today).sort((x, y) => x.date.localeCompare(y.date))[0];
  const pastDue = invoices.filter(i => ['sent', 'overdue'].includes(i.status) && Number(i.total) - Number(i.paid || 0) > 0 && (daysUntil(i.due_date) ?? 0) < 0)
    .reduce((t, i) => t + Number(i.total) - Number(i.paid || 0), 0);
  const since = new Date(); since.setDate(since.getDate() - 30);
  const sent30 = reports.filter(r => r.status === 'sent' && r.sent_at && new Date(String(r.sent_at).replace(' ', 'T')) >= since).length;
  const monthName = new Date(today + 'T12:00:00').toLocaleDateString('en-US', { month: 'long' });
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2.5">
      <Stat label="Worth a month" icon={<Receipt className="h-3 w-3" />}
        value={value && value.month > 0 ? `${value.estimated ? '~' : ''}${money(value.month)}` : '—'}
        sub={value && value.schedules > 0 ? `${value.schedules} running schedule${value.schedules === 1 ? '' : 's'} · ${sites.length} site${sites.length === 1 ? '' : 's'}` : 'Nothing on a schedule yet'} />
      <Stat label={monthName} icon={<CheckCircle2 className="h-3 w-3" />} value={`${done} done`} sub={booked ? `${booked} more booked` : 'nothing else booked'} tone={booked ? 'gray' : 'warn'} />
      <Stat label="Open balance" icon={<Receipt className="h-3 w-3" />} value={money(account.open_balance)}
        sub={pastDue > 0 ? `${money(pastDue)} past due` : Number(account.open_balance) > 0 ? 'within terms' : 'all paid'} tone={pastDue > 0 ? 'bad' : Number(account.open_balance) > 0 ? 'gray' : 'good'} />
      <Stat label="Next visit" icon={<Clock className="h-3 w-3" />} value={next ? shortDay(next.date) : 'None'}
        sub={next ? [next.site, next.time_start ? clock(next.time_start) : null, next.crew].filter(Boolean).join(' · ') : 'nothing booked'} tone={next ? 'gray' : 'warn'} />
      <Stat label="Reports" icon={<Camera className="h-3 w-3" />} value={`${sent30} sent`}
        sub={unreported.length ? `${unreported.length} visit${unreported.length === 1 ? '' : 's'} to send` : 'last 30 days · all sent'} tone={unreported.length ? 'warn' : 'good'} />
    </div>
  );
}

// ── Sites: the map and the cards ──────────────────────────────────────────────
const OSM_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const sitePin = (n: number, cancelled: boolean) => L.divIcon({
  className: '',
  html: `<div style="width:30px;height:30px;border-radius:12px;background:${cancelled ? '#9ca3af' : '#111827'};color:#fff;border:3px solid #fff;box-shadow:0 4px 12px -2px rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;font:900 13px/1 ui-sans-serif,system-ui">${n}</div>`,
  iconSize: [30, 30], iconAnchor: [15, 15], tooltipAnchor: [0, -16],
});

function SitesMap({ sites }: { sites: Site[] }) {
  // A site with no pin is left off (null would read as 0,0 — the Gulf of Guinea).
  const pins = sites.map((s, i) => ({ s, n: i + 1 })).filter(x => x.s.lat != null && x.s.lng != null && Number.isFinite(Number(x.s.lat)) && Number.isFinite(Number(x.s.lng)));
  const bounds = useMemo(() => (pins.length ? L.latLngBounds(pins.map(p => [Number(p.s.lat), Number(p.s.lng)] as [number, number])).pad(0.25) : null), [pins]);
  if (!bounds) {
    return (
      <div className="h-48 sm:h-full min-h-[200px] rounded-2xl bg-gray-100 flex items-center justify-center text-sm text-gray-500 px-4 text-center">
        No site has a pin yet — open a site and save its address to put it on the map.
      </div>
    );
  }
  return (
    <div className="h-56 sm:h-full min-h-[240px] rounded-2xl overflow-hidden border border-gray-200">
      <MapContainer bounds={bounds} maxZoom={17} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url={OSM_URL} />
        {pins.map(p => (
          <Marker key={p.s.id} position={[Number(p.s.lat), Number(p.s.lng)]} icon={sitePin(p.n, p.s.service_status === 'cancelled')}>
            <Tooltip direction="top">{p.s.label || p.s.address}</Tooltip>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}

const emptySite = {
  label: '', address: '', city: '', zip_code: '',
  site_contact_name: '', site_contact_phone: '', site_contact_email: '', gate_notes: '', access_notes: '',
};

function SitesCard({ accountId, sites, quotes = [], onNewBid }: { accountId: Id; sites: Site[]; quotes?: any[]; onNewBid: (propertyId: Id) => void }) {
  // The bids on each site — a pasted proposal lands as one draft bid per property (owner, Sep 28 2026).
  const navigate = useNavigate();
  const quotesFor = (propertyId: Id) => quotes.filter(q => Number(q.property_id) === Number(propertyId));
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Id | null>(null);
  const [nf, setNf] = useState(emptySite);
  const setN = (k: keyof typeof emptySite) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setNf(p => ({ ...p, [k]: e.target.value }));

  const add = useMutation(() => api.post(`/commercial/${accountId}/sites`, nf).then(r => r.data), {
    onSuccess: (d: any) => {
      toast.success(d?.message || 'Site added');
      setNf(emptySite); setAdding(false);
      qc.invalidateQueries(['commercial']);
    },
    onError: errToast('Could not add the site'),
  });

  return (
    <Section title={`Sites (${sites.length})`} icon={<Building2 className="h-4 w-4 text-gray-500" />} action={
      <button onClick={() => setAdding(v => !v)} disabled={readOnly} className="btn-secondary text-sm">
        <Plus className="h-4 w-4" /> Add site
      </button>
    }>
      {adding && (
        <div className="mb-4 rounded-2xl border-2 border-brand-200 p-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Address *"><input value={nf.address} onChange={setN('address')} className="input" /></Field>
            <Field label="Site name"><input value={nf.label} onChange={setN('label')} className="input" placeholder="e.g. North office" /></Field>
            <Field label="City"><input value={nf.city} onChange={setN('city')} className="input" /></Field>
            <Field label="Zip"><input value={nf.zip_code} onChange={setN('zip_code')} className="input" /></Field>
            <Field label="Site contact"><input value={nf.site_contact_name} onChange={setN('site_contact_name')} className="input" /></Field>
            <Field label="Site phone"><input value={nf.site_contact_phone} onChange={setN('site_contact_phone')} className="input" /></Field>
            <Field label="Site emails"><input inputMode="email" value={nf.site_contact_email} onChange={setN('site_contact_email')} className="input" placeholder="one, or several with commas" /></Field>
            <Field label="Gate notes"><input value={nf.gate_notes} onChange={setN('gate_notes')} className="input" /></Field>
          </div>
          <Field label="Access notes"><textarea value={nf.access_notes} onChange={setN('access_notes')} rows={2} className="input" /></Field>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setAdding(false); setNf(emptySite); }} className="btn-secondary">Cancel</button>
            <button onClick={() => add.mutate()} disabled={readOnly || !nf.address.trim() || add.isLoading} className="btn-primary">
              {add.isLoading ? 'Saving…' : 'Add site'}
            </button>
          </div>
        </div>
      )}

      {sites.length === 0 ? (
        <p className="text-sm text-gray-600">No sites yet. Add one, or paste the proposal and the sites come with it.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-4">
          <SitesMap sites={sites} />
          <ul className="space-y-2.5">
            {sites.map((s, i) => (
              <li key={s.id} className={`rounded-2xl border p-3 ${s.service_status === 'cancelled' ? 'border-gray-200 bg-gray-50' : 'border-gray-200 bg-white'}`}>
                {editing === s.id
                  ? <SiteEdit site={s} onDone={() => setEditing(null)} />
                  : (
                    <div className="flex items-start gap-3">
                      <span className={`h-7 w-7 rounded-xl text-white text-xs font-black flex items-center justify-center flex-shrink-0 mt-0.5 ${s.service_status === 'cancelled' ? 'bg-gray-400' : 'bg-gray-900'}`}>{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <p className="font-black text-gray-900 break-words leading-tight">
                          {s.label || s.address}
                          {s.service_status === 'cancelled' && <span className="ml-2 badge-skipped">Cancelled</span>}
                        </p>
                        {s.label && <p className="text-sm text-gray-600 break-words">{[s.address, s.city, s.zip_code].filter(Boolean).join(', ')}</p>}
                        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-sm">
                          <span className={s.next_visit ? 'text-gray-900 font-semibold' : 'text-amber-700 font-semibold'}>
                            {s.next_visit ? `Next ${shortDay(s.next_visit)}` : 'No visit booked'}
                          </span>
                          {s.frequency && <span className="text-gray-600">{s.frequency}</span>}
                          {s.route_name && <span className="text-gray-600">{s.route_name}</span>}
                          {s.last_visit && <span className="text-gray-500">last {shortDay(s.last_visit)}</span>}
                          {s.mowing_price != null && Number(s.mowing_price) > 0 && <span className="text-gray-700 tabular-nums">{money(s.mowing_price)} a visit</span>}
                        </div>
                        {(s.site_contact_name || s.site_contact_phone || s.site_contact_email) ? (
                          <p className="text-sm text-gray-600 break-words mt-1">
                            {[s.site_contact_name, s.site_contact_phone, s.site_contact_email].filter(Boolean).join(' · ')}
                          </p>
                        ) : (
                          <p className="text-xs text-amber-700 font-semibold mt-1 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> No site contact — reports go only to the account list.</p>
                        )}
                        {(s.gate_notes || s.access_notes) && (
                          <p className="text-xs text-gray-600 break-words mt-0.5">{[s.gate_notes, s.access_notes].filter(Boolean).join(' · ')}</p>
                        )}
                        {quotesFor(s.id).length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {quotesFor(s.id).map((q: any) => (
                              <button key={q.id} type="button" onClick={() => navigate('/quotes', { state: { openQuoteId: q.id } })}
                                title="Open the bid"
                                className={`text-xs px-2 py-1 rounded-lg border font-semibold ${q.status === 'accepted' ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : q.status === 'sent' ? 'border-brand-300 bg-brand-50 text-brand-800' : 'border-gray-300 bg-white text-gray-700'}`}>
                                <FileText className="inline h-3 w-3 -mt-0.5 mr-1" />
                                {q.status === 'accepted' ? 'Signed' : q.status === 'sent' ? 'Bid out' : 'Draft bid'}{q.one_time > 0 ? ` · ${money(q.one_time)} one-time` : ''}{q.per_visit ? ` · ${q.per_visit}` : ''}
                              </button>
                            ))}
                          </div>
                        )}
                        {/* The site map (owner, Oct 2 2026): what gets done where, and the locks with their codes for the crew. */}
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          <Link to={`/site-map/${s.id}`} className="btn-secondary text-xs py-1 min-h-0 inline-flex items-center gap-1">
                            <MapIcon className="h-3.5 w-3.5" /> Site map
                            {((s.map_areas || 0) > 0 || (s.map_locks || 0) > 0) && (
                              <span className="text-gray-500 font-semibold">
                                · {[s.map_areas ? `${s.map_areas} area${s.map_areas === 1 ? '' : 's'}` : null, s.map_locks ? `${s.map_locks} lock${s.map_locks === 1 ? '' : 's'}` : null].filter(Boolean).join(', ')}
                              </span>
                            )}
                          </Link>
                          {s.service_status !== 'cancelled' && (
                            <button type="button" onClick={() => onNewBid(s.id)} disabled={readOnly} className="btn-secondary text-xs py-1 min-h-0 inline-flex items-center gap-1">
                              <Sparkles className="h-3.5 w-3.5" /> New bid
                            </button>
                          )}
                        </div>
                      </div>
                      <button onClick={() => setEditing(s.id)} disabled={readOnly} aria-label="Edit site"
                        className="h-9 w-9 flex items-center justify-center rounded-2xl text-gray-600 hover:bg-gray-100 disabled:opacity-40 flex-shrink-0">
                        <Edit2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="mt-3 text-xs text-gray-600">
        To put a site on a route or set its mowing schedule, use <Link to="/routes" className="underline">Routes</Link> or the customer record as usual.
      </p>
    </Section>
  );
}

// ── Activity: what happened, what is coming ───────────────────────────────────
type Event = { key: string; at: string; when: string; icon: React.ReactNode; title: string; detail?: string | null; tone: 'done' | 'booked' | 'bad' | 'money' | 'bid'; href?: string; state?: any };

function ActivityCard({ visits, reports, invoices, quotes }: { visits: Visit[]; reports: Report[]; invoices: Invoice[]; quotes: any[] }) {
  const navigate = useNavigate();
  const [all, setAll] = useState(false);
  const today = businessToday();
  const events: Event[] = [];
  for (const v of visits) {
    const what = v.title || (v.service_type === 'mowing' ? 'Mowing' : v.service_type);
    if (v.status === 'complete') {
      events.push({
        key: `v${v.id}`, at: v.date, when: shortDay(v.date), tone: 'done', icon: <CheckCircle2 className="h-4 w-4" />,
        title: `${what} · ${v.site || 'site'}`,
        detail: [v.crew, v.photos ? `${v.photos} photo${v.photos === 1 ? '' : 's'}` : 'no photos',
          v.report === 'sent' ? 'reported' : v.report === 'failed' ? 'report failed' : 'not reported'].filter(Boolean).join(' · '),
        href: `/jobs/${v.id}`,
      });
    } else if (v.status === 'skipped') {
      events.push({ key: `v${v.id}`, at: v.date, when: shortDay(v.date), tone: 'bad', icon: <AlertTriangle className="h-4 w-4" />, title: `Skipped · ${v.site || 'site'}`, detail: what, href: `/jobs/${v.id}` });
    } else if (v.date >= today) {
      events.push({
        key: `v${v.id}`, at: v.date, when: shortDay(v.date), tone: 'booked', icon: v.status === 'in_progress' ? <Clock className="h-4 w-4" /> : <Calendar className="h-4 w-4" />,
        title: `${v.status === 'in_progress' ? 'On site now' : 'Booked'} · ${v.site || 'site'}`,
        detail: [what, v.time_start ? clock(v.time_start) : null, v.crew || 'nobody assigned yet'].filter(Boolean).join(' · '),
        href: `/jobs/${v.id}`,
      });
    }
  }
  for (const r of reports) if (r.status === 'failed') {
    events.push({ key: `r${r.id}`, at: localDay(r.created_at) || r.date || today, when: shortDay(localDay(r.created_at) || r.date), tone: 'bad', icon: <Mail className="h-4 w-4" />, title: `Report failed · ${r.site || 'site'}`, detail: r.error });
  }
  for (const i of invoices) {
    const owed = Math.max(0, Number(i.total) - Number(i.paid || 0));
    const late = ['sent', 'overdue'].includes(i.status) && owed > 0 && (daysUntil(i.due_date) ?? 0) < 0;
    events.push({
      key: `i${i.id}`, at: localDay(i.paid_at || i.created_at), when: shortDay(localDay(i.paid_at || i.created_at)), tone: late ? 'bad' : 'money', icon: <Receipt className="h-4 w-4" />,
      title: `${i.invoice_number} · ${money(i.total)}${Number(i.paid) > 0 && owed <= 0 ? ' paid' : i.status === 'draft' ? ' draft' : late ? ' past due' : ' sent'}`,
      detail: i.billing_period_start ? `${dayLabel(i.billing_period_start)} – ${dayLabel(i.billing_period_end)}${i.due_date ? ` · due ${dayLabel(i.due_date)}` : ''}` : i.due_date ? `due ${dayLabel(i.due_date)}` : null,
      href: '/billing',
    });
  }
  for (const q of quotes) {
    const at = localDay(q.accepted_at || q.sent_at || q.created_at);
    events.push({
      key: `q${q.id}`, at, when: shortDay(at), tone: 'bid', icon: <FileText className="h-4 w-4" />,
      title: `${q.status === 'accepted' ? 'Bid signed' : q.status === 'sent' ? 'Bid sent' : 'Draft bid'} · ${q.title || 'bid'}`,
      detail: [q.one_time > 0 ? `${money(q.one_time)} one-time` : null, q.per_visit].filter(Boolean).join(' · ') || null,
      state: { openQuoteId: q.id },
    });
  }
  // Coming up is only what is booked; a bid or a bill made today is 'lately' however the clock reads.
  const coming = events.filter(e => e.tone === 'booked' && e.at >= today).sort((x, y) => x.at.localeCompare(y.at));
  const past = events.filter(e => !coming.includes(e)).sort((x, y) => y.at.localeCompare(x.at));
  const shown = all ? past : past.slice(0, 8);
  const skin = (t: Event['tone']) => t === 'done' ? 'bg-emerald-50 text-emerald-700' : t === 'booked' ? 'bg-blue-50 text-blue-700' : t === 'bad' ? 'bg-red-50 text-red-700' : t === 'money' ? 'bg-gray-100 text-gray-700' : 'bg-brand-50 text-brand-700';
  const Row = ({ e }: { e: Event }) => {
    const body = (
      <>
        <span className={`h-8 w-8 rounded-xl flex items-center justify-center flex-shrink-0 ${skin(e.tone)}`}>{e.icon}</span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-gray-900 truncate">{e.title}</span>
          {e.detail && <span className="block text-xs text-gray-600 truncate">{e.detail}</span>}
        </span>
        <span className="text-xs text-gray-500 whitespace-nowrap tabular-nums">{e.when}</span>
      </>
    );
    const cls = 'w-full flex items-center gap-3 py-2 text-left rounded-xl px-1 -mx-1 hover:bg-gray-50';
    if (e.state) return <button type="button" onClick={() => navigate('/quotes', { state: e.state })} className={cls}>{body}</button>;
    if (e.href) return <Link to={e.href} className={cls}>{body}</Link>;
    return <div className={cls}>{body}</div>;
  };
  return (
    <Section title="Activity" icon={<Clock className="h-4 w-4 text-gray-500" />}>
      {!events.length && <p className="text-sm text-gray-600">Nothing yet. The first visit, bid or bill shows up here.</p>}
      {coming.length > 0 && (
        <>
          <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1">Coming up</p>
          <ul className="divide-y divide-gray-100 mb-3">{coming.slice(0, 6).map(e => <li key={e.key}><Row e={e} /></li>)}</ul>
        </>
      )}
      {past.length > 0 && (
        <>
          <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1">Lately</p>
          <ul className="divide-y divide-gray-100">{shown.map(e => <li key={e.key}><Row e={e} /></li>)}</ul>
          {past.length > 8 && (
            <button type="button" onClick={() => setAll(v => !v)} className="mt-2 text-sm font-bold text-brand-700 underline">
              {all ? 'Show less' : `Show all ${past.length}`}
            </button>
          )}
        </>
      )}
    </Section>
  );
}

// ── More AP contacts ──────────────────────────────────────────────────────────
// Owner, Sep 30 2026: "just give me the ability to add a whole billing address
// like a new AP contact — AP emails, AP phone". Each one gets every bill, on top
// of the AP emails in the Billing card.
const blankContact = { name: '', email: '', phone: '', address: '' };
type ContactForm = typeof blankContact;
const formOf = (c: Contact): ContactForm => ({ name: c.name || '', email: c.email || '', phone: c.phone || '', address: c.address || '' });

function ContactFields({ f, setF }: { f: ContactForm; setF: (f: ContactForm) => void }) {
  const set = (k: keyof ContactForm) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      <Field label="AP contact"><input value={f.name} onChange={set('name')} className="input" /></Field>
      <Field label="AP emails"><input value={f.email} onChange={set('email')} className="input" inputMode="email" placeholder="ap@company.com, other@company.com" /></Field>
      <Field label="AP phone"><input value={f.phone} onChange={set('phone')} className="input" inputMode="tel" /></Field>
      <Field label="Billing address" className="sm:col-span-3">
        <input value={f.address} onChange={set('address')} className="input" placeholder="Street, suite, city, state, zip" />
      </Field>
    </div>
  );
}

function ContactsCard({ accountId, contacts }: { accountId: Id; contacts: Contact[] }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const [adding, setAdding] = useState(false);
  const [nf, setNf] = useState<ContactForm>(blankContact);
  const [editing, setEditing] = useState<Id | null>(null);
  const [ef, setEf] = useState<ContactForm>(blankContact);
  const done = (d: any) => { toast.success(d?.message || 'Saved'); qc.invalidateQueries(['commercial', String(accountId)]); };
  // Every AP contact gets the bills — that is what one is for.
  const body = (f: ContactForm) => ({ ...f, gets_bills: !!f.email.trim() });
  const add = useMutation((f: ContactForm) => api.post(`/commercial/${accountId}/contacts`, body(f)).then(r => r.data), {
    onSuccess: (d: any) => { done(d); setAdding(false); setNf(blankContact); }, onError: errToast('Could not add the AP contact'),
  });
  const save = useMutation(({ id, f }: { id: Id; f: ContactForm }) => api.put(`/commercial/contacts/${id}`, body(f)).then(r => r.data), {
    onSuccess: (d: any) => { done(d); setEditing(null); }, onError: errToast('Could not save the AP contact'),
  });
  const del = useMutation((id: Id) => api.delete(`/commercial/contacts/${id}`).then(r => r.data), {
    onSuccess: done, onError: errToast('Could not remove the AP contact'),
  });

  return (
    <Section title="More AP contacts" icon={<Mail className="h-4 w-4" />}
      summary={contacts.length ? contacts.map(c => c.name || c.email || c.phone).filter(Boolean).join(' · ') : 'Another AP person or billing office gets every bill too'}
      action={!adding && (
      <button onClick={() => setAdding(true)} disabled={readOnly} className="btn-secondary text-sm">+ Add AP contact</button>
    )}>
      {adding && (
        <div className="mb-3 rounded-lg border border-gray-200 p-3">
          <ContactFields f={nf} setF={setNf} />
          <div className="mt-2 flex gap-2">
            <button onClick={() => add.mutate(nf)} disabled={readOnly || add.isLoading} className="btn-primary text-sm">Add</button>
            <button onClick={() => { setAdding(false); setNf(blankContact); }} className="btn-secondary text-sm">Cancel</button>
          </div>
        </div>
      )}
      {!contacts.length && !adding && (
        <p className="text-sm text-gray-600">Another AP person or billing office? Add them here — every bill is emailed to them too.</p>
      )}
      <ul className="divide-y divide-gray-100">
        {contacts.map(c => (
          <li key={c.id} className="py-2">
            {editing === c.id ? (
              <div>
                <ContactFields f={ef} setF={setEf} />
                <div className="mt-2 flex gap-2">
                  <button onClick={() => save.mutate({ id: c.id, f: ef })} disabled={readOnly || save.isLoading} className="btn-primary text-sm">Save</button>
                  <button onClick={() => setEditing(null)} className="btn-secondary text-sm">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 text-sm">
                  <p className="font-semibold text-gray-900 break-words">{c.name || c.email || c.phone}</p>
                  <p className="text-gray-700 break-words">
                    {c.email && <a href={`mailto:${c.email}`} className="underline">{c.email}</a>}
                    {c.phone && c.email ? ' · ' : ''}
                    {c.phone && <a href={`tel:${c.phone}`} className="underline">{c.phone}</a>}
                  </p>
                  {c.address && <p className="text-gray-600 break-words">{c.address}</p>}
                  <p className="text-xs text-gray-600">{c.gets_bills ? 'Gets every bill' : 'No email — does not get bills'}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setEditing(c.id); setEf(formOf(c)); }} disabled={readOnly} className="text-sm font-bold text-brand-700 underline">Edit</button>
                  <button onClick={() => { if (window.confirm(`Remove ${c.name || c.email || 'this AP contact'}?`)) del.mutate(c.id); }}
                    disabled={readOnly || del.isLoading} className="text-sm font-bold text-red-700 underline">Remove</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Section>
  );
}

// ── Remove ───────────────────────────────────────────────────────────────────
// Delete when nothing ever happened on it (a test or a mistake); otherwise
// archive, which keeps the history and stops the schedules.
function RemoveCard({ accountId, company }: { accountId: Id; company: string }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const readOnly = useReadOnly();
  const [open, setOpen] = useState(false);
  const { data: f, isLoading } = useQuery(['commercial', String(accountId), 'remove'],
    () => api.get(`/commercial/${accountId}/remove`).then(r => r.data), { enabled: open });
  const remove = useMutation((mode: 'delete' | 'archive') => api.post(`/commercial/${accountId}/remove`, { mode }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Removed'); qc.invalidateQueries(['commercial']); nav('/commercial'); },
    onError: errToast('Could not remove the account'),
  });
  const go = (mode: 'delete' | 'archive') => {
    const ask = mode === 'delete'
      ? `Delete ${company} for good? Its sites, upcoming visits and draft bids go with it. This cannot be undone.`
      : `Archive ${company}? Bills and history stay; schedules stop and upcoming visits are skipped.`;
    if (window.confirm(ask)) remove.mutate(mode);
  };

  return (
    <Section title="Remove account" icon={<Trash2 className="h-4 w-4" />} summary="Archive it, or delete it if nothing ever happened here">
      {!open ? (
        <button onClick={() => setOpen(true)} disabled={readOnly} className="btn-secondary text-sm text-red-700">Remove this account…</button>
      ) : isLoading || !f ? (
        <p className="text-sm text-gray-500">Checking…</p>
      ) : (
        <div className="space-y-3 text-sm">
          <p className="text-gray-700">
            {f.sites} site{f.sites === 1 ? '' : 's'} · {f.upcoming} upcoming visit{f.upcoming === 1 ? '' : 's'} · {f.worked} worked · {f.invoices} bill{f.invoices === 1 ? '' : 's'}
          </p>
          <div className="flex flex-wrap gap-2">
            {f.can_delete && (
              <button onClick={() => go('delete')} disabled={readOnly || remove.isLoading} className="btn-primary bg-red-600 hover:bg-red-700 text-sm">Delete for good</button>
            )}
            <button onClick={() => go('archive')} disabled={readOnly || remove.isLoading} className="btn-secondary text-sm">Archive</button>
            <button onClick={() => setOpen(false)} className="btn-secondary text-sm">Cancel</button>
          </div>
          <p className="text-xs text-gray-600">
            {f.can_delete
              ? 'Nothing was ever worked or billed here, so it can be deleted outright. Archive keeps it restorable.'
              : 'It has history, so it can only be archived — bills, pay and reports stay. Restore it from Customers → Archived.'}
          </p>
        </div>
      )}
    </Section>
  );
}

// ── Billing ──────────────────────────────────────────────────────────────────
function billingFrom(a: any) {
  return {
    billing_contact_name: a.billing_contact_name || '',
    billing_email: a.billing_email || '',
    billing_phone: a.billing_phone || '',
    billing_address: a.billing_address || '',
    default_po_number: a.default_po_number || '',
    payment_terms: a.payment_terms == null ? 30 : Number(a.payment_terms),
    commercial_pricing: (a.commercial_pricing === 'monthly' ? 'monthly' : 'per_visit') as 'monthly' | 'per_visit',
    contract_monthly_amount: a.contract_monthly_amount != null ? String(a.contract_monthly_amount) : '',
    contract_start: day(a.contract_start),
    contract_end: day(a.contract_end),
    contract_auto_renew: !!Number(a.contract_auto_renew),
  };
}

function BillingCard({ account }: { account: any }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const [f, setF] = useState(() => billingFrom(account));
  // Reload the form when fresh data lands (after a save elsewhere on the page).
  // Loaded once per account: a refresh after a document upload or a report must not wipe unsaved billing edits.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setF(billingFrom(account)); }, [account.id]);
  const set = (k: keyof ReturnType<typeof billingFrom>) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF(prev => ({ ...prev, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }));

  const save = useMutation(() => api.put(`/commercial/${account.id}`, {
    billing_contact_name: f.billing_contact_name, billing_email: f.billing_email, billing_phone: f.billing_phone, billing_address: f.billing_address,
    default_po_number: f.default_po_number, payment_terms: Number(f.payment_terms),
    commercial_pricing: f.commercial_pricing,
    ...(f.commercial_pricing === 'monthly' ? { contract_monthly_amount: Number(f.contract_monthly_amount) || 0 } : {}),
    contract_start: f.contract_start || null, contract_end: f.contract_end || null,
    contract_auto_renew: f.contract_auto_renew,
  }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Saved'); qc.invalidateQueries(['commercial']); },
    onError: errToast('Could not save'),
  });

  const monthly = f.commercial_pricing === 'monthly';
  return (
    <Section title="Billing & terms" icon={<Receipt className="h-4 w-4" />}
      summary={[pricingLabel(account), termsLabel(account.payment_terms), account.default_po_number ? `PO ${account.default_po_number}` : null,
        account.billing_email ? `bills to ${account.billing_email}` : 'no AP email yet'].filter(Boolean).join(' · ')}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="AP contact"><input value={f.billing_contact_name} onChange={set('billing_contact_name')} className="input" /></Field>
        <Field label="AP emails — every bill goes to all"><input inputMode="email" value={f.billing_email} onChange={set('billing_email')} className="input" placeholder="ap@company.com, owner@company.com" /></Field>
        <Field label="AP phone"><input value={f.billing_phone} onChange={set('billing_phone')} className="input" /></Field>
        <Field label="Billing address" className="sm:col-span-3">
          <input value={f.billing_address} onChange={set('billing_address')} className="input" placeholder="Street, suite, city, state, zip — shows on the bill" />
        </Field>
        <Field label="Default PO"><input value={f.default_po_number} onChange={set('default_po_number')} className="input" /></Field>
        <Field label="Terms">
          <select value={f.payment_terms} onChange={set('payment_terms')} className="input">
            {TERMS_OPTIONS.map(t => <option key={t} value={t}>{termsLabel(t)}</option>)}
          </select>
        </Field>
      </div>

      <div className="mt-4">
        <p className="label-sm">Pricing</p>
        <div className="flex flex-wrap gap-4 text-sm text-gray-900">
          <label className="flex items-center gap-2">
            <input type="radio" name="pricing" checked={!monthly} onChange={() => setF(p => ({ ...p, commercial_pricing: 'per_visit' }))} className="h-4 w-4" />
            Per visit
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="pricing" checked={monthly} onChange={() => setF(p => ({ ...p, commercial_pricing: 'monthly' }))} className="h-4 w-4" />
            Flat monthly contract
          </label>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {monthly && (
          <Field label="Per month *">
            <input type="number" inputMode="decimal" min="0" step="0.01" value={f.contract_monthly_amount} onChange={set('contract_monthly_amount')} className="input" placeholder="0.00" />
          </Field>
        )}
        <Field label="Contract start"><input type="date" value={f.contract_start} onChange={set('contract_start')} className="input" /></Field>
        <Field label="Contract end"><input type="date" value={f.contract_end} onChange={set('contract_end')} className="input" /></Field>
        <label className="flex items-center gap-2 text-sm text-gray-900 self-end pb-2.5">
          <input type="checkbox" checked={f.contract_auto_renew} onChange={set('contract_auto_renew')} className="h-4 w-4" /> Auto-renews
        </label>
      </div>

      <p className="mt-3 text-xs text-gray-600">
        {monthly
          ? 'Flat monthly: one contract line a month. Mows show as $0 "included". Other work bills on top.'
          : 'Per visit: each mow bills at its price, as usual.'}
        {' '}Bills go to the AP email with the PO and terms.
      </p>
      <div className="mt-3 flex justify-end">
        <button onClick={() => save.mutate()}
          disabled={readOnly || save.isLoading || (monthly && !(Number(f.contract_monthly_amount) > 0))}
          className="btn-primary">{save.isLoading ? 'Saving…' : 'Save billing'}</button>
      </div>
    </Section>
  );
}

function SiteEdit({ site, onDone }: { site: Site; onDone: () => void }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const [f, setF] = useState({
    label: site.label || '', site_contact_name: site.site_contact_name || '', site_contact_phone: site.site_contact_phone || '',
    site_contact_email: site.site_contact_email || '', gate_notes: site.gate_notes || '', access_notes: site.access_notes || '',
    address: site.address || '', city: site.city || '', zip_code: site.zip_code || '',
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF(p => ({ ...p, [k]: e.target.value }));
  const save = useMutation(() => api.put(`/commercial/sites/${site.id}`, f).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Site saved'); qc.invalidateQueries(['commercial']); onDone(); },
    onError: errToast('Could not save the site'),
  });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-3">
        <Field label="Address"><input value={f.address} onChange={set('address')} className="input" placeholder="13740 W Hwy 29" /></Field>
        <Field label="City"><input value={f.city} onChange={set('city')} className="input" placeholder="Liberty Hill" /></Field>
        <Field label="Zip"><input inputMode="numeric" value={f.zip_code} onChange={set('zip_code')} className="input" placeholder="78642" /></Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Site name"><input value={f.label} onChange={set('label')} className="input" /></Field>
        <Field label="Site contact"><input value={f.site_contact_name} onChange={set('site_contact_name')} className="input" /></Field>
        <Field label="Site phone"><input value={f.site_contact_phone} onChange={set('site_contact_phone')} className="input" /></Field>
        <Field label="Site emails"><input inputMode="email" value={f.site_contact_email} onChange={set('site_contact_email')} className="input" placeholder="one, or several with commas" /></Field>
        <Field label="Gate notes"><input value={f.gate_notes} onChange={set('gate_notes')} className="input" /></Field>
      </div>
      <Field label="Access notes"><textarea value={f.access_notes} onChange={set('access_notes')} rows={2} className="input" /></Field>
      <div className="flex justify-end gap-2">
        <button onClick={onDone} className="btn-secondary">Cancel</button>
        <button onClick={() => save.mutate()} disabled={readOnly || save.isLoading} className="btn-primary">{save.isLoading ? 'Saving…' : 'Save site'}</button>
      </div>
    </div>
  );
}

// ── Service reports ─────────────────────────────────────────────────────────
function ReportsCard({ account, reports, unreported }: { account: any; reports: Report[]; unreported: Unreported[] }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const on = account.service_reports_on == null ? true : !!Number(account.service_reports_on);
  const [emails, setEmails] = useState(account.report_emails || '');
  useEffect(() => { setEmails(account.report_emails || ''); }, [account.report_emails]);

  const put = useMutation((body: any) => api.put(`/commercial/${account.id}`, body).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Saved'); qc.invalidateQueries(['commercial']); },
    onError: errToast('Could not save'),
  });
  const send = useMutation(({ jobId, note }: { jobId: Id; note?: string }) =>
    api.post(`/commercial/reports/job/${jobId}/send`, note ? { note } : {}).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Report sent'); qc.invalidateQueries(['commercial']); },
    onError: (e: any) => { errToast('Could not send the report')(e); qc.invalidateQueries(['commercial', String(account.id)]); },
  });
  const sendWithNote = (jobId: Id) => {
    const note = window.prompt('Add a note for the customer? (optional)', '');
    if (note === null) return;
    send.mutate({ jobId, note: note.trim() || undefined });
  };

  return (
    <Section title="Service reports" icon={<Camera className="h-4 w-4" />}
      summary={`${on ? 'A photo report after every visit' : 'Reports are off'} · ${reports.length} sent${unreported.length ? ` · ${unreported.length} to send` : ''}`}
      defaultOpen={unreported.length > 0 || reports.some(r => r.status === 'failed')}>
      <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <input type="checkbox" checked={on} disabled={readOnly || put.isLoading}
          onChange={e => put.mutate({ service_reports_on: e.target.checked })} className="h-4 w-4" />
        Send a report after every visit
      </label>
      <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-end">
        <Field label="Also send reports to" className="flex-1">
          <input value={emails} onChange={e => setEmails(e.target.value)} className="input" placeholder="pm@company.com, owner@company.com" />
        </Field>
        <button onClick={() => put.mutate({ report_emails: emails })}
          disabled={readOnly || put.isLoading || emails === (account.report_emails || '')} className="btn-secondary">Save list</button>
      </div>
      <p className="mt-2 text-xs text-gray-600">
        A report goes about 30 minutes after a visit is done, so the crew's photos are in it. It goes to the site
        contact's email plus this list. No prices or crew notes are included.
      </p>

      <h3 className="mt-4 mb-2 text-sm font-black text-gray-900">Recent reports</h3>
      {reports.length === 0 ? (
        <p className="text-sm text-gray-600">None sent yet.</p>
      ) : (
        <ul className="divide-y divide-gray-200">
          {reports.map(r => (
            <li key={r.id} className="py-2.5 flex flex-wrap items-start gap-x-3 gap-y-1">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-gray-900 break-words">
                  {dayLabel(r.date)} · {r.site || 'Site'}
                  <span className="ml-2"><StatusChip status={r.status} /></span>
                </p>
                <p className="text-xs text-gray-600 break-words">
                  {r.photos} photo{r.photos === 1 ? '' : 's'}
                  {r.sent_to ? ` · to ${r.sent_to}` : ''}
                  {r.sent_at ? ` · ${fmtTs(r.sent_at, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
                </p>
                {r.status === 'failed' && r.error && <p className="text-xs text-red-700 font-semibold break-words">{r.error}</p>}
                {r.note && <p className="text-xs text-gray-700 italic break-words">Note: {r.note}</p>}
              </div>
              <div className="flex gap-2">
                {r.url && (
                  <a href={absUrl(r.url)} target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm px-3">View</a>
                )}
                <button onClick={() => sendWithNote(r.job_id)} disabled={readOnly || send.isLoading} className="btn-secondary text-sm px-3">
                  <Send className="h-3.5 w-3.5" /> Resend
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {unreported.length > 0 && (
        <>
          <h3 className="mt-4 mb-2 text-sm font-black text-gray-900">Visits not reported yet</h3>
          <ul className="divide-y divide-gray-200">
            {unreported.map(u => (
              <li key={u.job_id} className="py-2.5 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-gray-900 break-words">{dayLabel(u.date)} · {u.site || 'Site'}</p>
                  <p className="text-xs text-gray-600">{u.photos} photo{u.photos === 1 ? '' : 's'}</p>
                </div>
                <button onClick={() => sendWithNote(u.job_id)} disabled={readOnly || send.isLoading} className="btn-primary text-sm px-3">
                  <Send className="h-3.5 w-3.5" /> Send report
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </Section>
  );
}

function StatusChip({ status }: { status: string }) {
  if (status === 'sent') return <span className="badge-complete">Sent</span>;
  if (status === 'failed') return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-50 text-red-800 border border-red-200">Failed</span>;
  return <span className="badge-scheduled">Waiting</span>;
}

// ── Documents ───────────────────────────────────────────────────────────────
function DocumentsCard({ accountId, documents }: { accountId: Id; documents: Doc[] }) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const fileRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<Doc['kind']>('coi');
  const [title, setTitle] = useState('');
  const [expires, setExpires] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [editing, setEditing] = useState<Id | null>(null);
  const [editExp, setEditExp] = useState('');

  const upload = useMutation(() => {
    const fd = new FormData();
    fd.append('file', file as File);
    fd.append('kind', kind);
    if (title.trim()) fd.append('title', title.trim());
    if (expires) fd.append('expires_on', expires);
    return api.post(`/commercial/${accountId}/documents`, fd).then(r => r.data);
  }, {
    onSuccess: (d: any) => {
      toast.success(d?.message || 'Saved');
      setTitle(''); setExpires(''); setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      qc.invalidateQueries(['commercial']);
    },
    onError: errToast('Upload failed'),
  });
  const update = useMutation(({ docId, body }: { docId: Id; body: any }) => api.put(`/commercial/documents/${docId}`, body).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Saved'); setEditing(null); qc.invalidateQueries(['commercial']); },
    onError: errToast('Could not save'),
  });
  const remove = useMutation((docId: Id) => api.delete(`/commercial/documents/${docId}`).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Removed'); qc.invalidateQueries(['commercial']); },
    onError: errToast('Could not remove'),
  });

  const tooBig = !!file && file.size > 15 * 1024 * 1024;
  return (
    <Section title="Papers" icon={<ShieldCheck className="h-4 w-4" />}
      summary={documents.length ? documents.map(d => `${docKindLabel(d.kind)}${d.expires_on ? ` to ${dayLabel(d.expires_on)}` : ''}`).join(' · ') : 'No COI or W-9 on file yet'}
      defaultOpen={documents.some(d => expiryTone(d.expires_on) === 'expired')}>
      <div className="rounded-2xl border-2 border-gray-200 p-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label="Kind">
            <select value={kind} onChange={e => setKind(e.target.value as Doc['kind'])} className="input">
              {DOC_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </Field>
          <Field label="Expires on"><input type="date" value={expires} onChange={e => setExpires(e.target.value)} className="input" /></Field>
          <Field label="Title" className="col-span-2"><input value={title} onChange={e => setTitle(e.target.value)} className="input" placeholder="e.g. 2026 COI — State Farm" /></Field>
        </div>
        <div className="mt-3 flex flex-col sm:flex-row gap-2 sm:items-center">
          <input ref={fileRef} type="file" accept="application/pdf,image/*" disabled={readOnly}
            onChange={e => setFile(e.target.files?.[0] || null)}
            className="block w-full min-w-0 text-sm text-gray-700 file:mr-3 file:rounded-xl file:border-0 file:bg-brand-50 file:px-3 file:py-2 file:font-bold file:text-brand-800" />
          <button onClick={() => upload.mutate()} disabled={readOnly || !file || tooBig || upload.isLoading} className="btn-primary flex-shrink-0">
            <Upload className="h-4 w-4" /> {upload.isLoading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
        {tooBig && <p className="mt-2 text-xs text-red-700 font-semibold">That file is over 15 MB.</p>}
        <p className="mt-2 text-xs text-gray-600">PDF or photo, up to 15 MB.</p>
      </div>

      {documents.length === 0 ? (
        <p className="mt-3 text-sm text-gray-600">No documents yet. Most commercial customers want a COI and a W-9 on file.</p>
      ) : (
        <ul className="mt-3 divide-y divide-gray-200">
          {documents.map(d => {
            const tone = expiryTone(d.expires_on);
            const n = daysUntil(d.expires_on);
            return (
              <li key={d.id} className="py-2.5 flex flex-wrap items-start gap-x-3 gap-y-2">
                <FileText className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-gray-900 break-words">
                    <span className="badge-claimed mr-2">{docKindLabel(d.kind)}</span>
                    {d.title || 'Untitled'}
                  </p>
                  {editing === d.id ? (
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <input type="date" value={editExp} onChange={e => setEditExp(e.target.value)} className="input w-auto" />
                      <button onClick={() => update.mutate({ docId: d.id, body: { expires_on: editExp || null } })}
                        disabled={readOnly || update.isLoading} className="btn-primary text-sm px-3">Save</button>
                      <button onClick={() => setEditing(null)} className="btn-secondary text-sm px-3">Cancel</button>
                    </div>
                  ) : (
                    <p className={`text-xs ${tone === 'expired' ? 'text-red-700 font-bold' : tone === 'soon' ? 'text-amber-700 font-bold' : 'text-gray-600'}`}>
                      {tone === 'none' ? 'No expiry'
                        : tone === 'expired' ? `Expired ${dayLabel(d.expires_on)}`
                        : tone === 'soon' ? `Expires ${dayLabel(d.expires_on)} (${n} day${n === 1 ? '' : 's'})`
                        : `Expires ${dayLabel(d.expires_on)}`}
                      {d.uploaded_at ? ` · added ${dayLabel(d.uploaded_at)}` : ''}
                    </p>
                  )}
                  {d.notes && <p className="text-xs text-gray-600 break-words">{d.notes}</p>}
                </div>
                {editing !== d.id && (
                  <div className="flex gap-2">
                    {d.url && <a href={absUrl(d.url)} target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm px-3">View</a>}
                    <button onClick={() => { setEditing(d.id); setEditExp(day(d.expires_on)); }} disabled={readOnly}
                      className="btn-secondary text-sm px-3">Expiry</button>
                    <button aria-label="Delete document" disabled={readOnly || remove.isLoading}
                      onClick={() => { if (window.confirm(`Delete "${d.title || docKindLabel(d.kind)}"? This can't be undone.`)) remove.mutate(d.id); }}
                      className="btn-secondary text-sm px-3 text-red-700"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

// ── Invoices ────────────────────────────────────────────────────────────────
function InvoicesCard({ account, invoices, contractBills }: {
  account: any; invoices: Invoice[]; contractBills: Detail['contract_bills'];
}) {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const [editing, setEditing] = useState<Id | null>(null);
  const [po, setPo] = useState('');
  const savePo = useMutation(({ invId, value }: { invId: Id; value: string }) =>
    api.put(`/invoices/${invId}/po`, { po_number: value }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Saved'); setEditing(null); qc.invalidateQueries(['commercial']); },
    onError: errToast('Could not save the PO'),
  });
  const monthly = account.commercial_pricing === 'monthly';

  return (
    <Section title="Bills" icon={<FileText className="h-4 w-4" />}
      summary={invoices.length ? `${invoices.length} bill${invoices.length === 1 ? '' : 's'} · ${money(account.open_balance)} open` : 'No bills yet'}
      action={<Link to="/billing" className="text-sm font-bold text-brand-700 underline">Open Billing</Link>}>
      {monthly && contractBills.length > 0 && (
        <p className="mb-3 text-xs text-gray-600">
          Contract billed: {contractBills.map(c => `${c.month} ${money(c.amount)}`).join(' · ')}
        </p>
      )}
      {invoices.length === 0 ? (
        <p className="text-sm text-gray-600">No bills yet.</p>
      ) : (
        <ul className="divide-y divide-gray-200">
          {invoices.map(inv => {
            const owed = Math.max(0, Number(inv.total) - Number(inv.paid || 0));
            const late = (inv.status === 'sent' || inv.status === 'overdue') && owed > 0 && (daysUntil(inv.due_date) ?? 0) < 0;
            return (
              <li key={inv.id} className="py-2.5">
                <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-900">
                      {inv.invoice_number} <span className="ml-1 text-xs font-semibold uppercase text-gray-600">{inv.status}</span>
                      {late && <span className="ml-2 inline-flex items-center gap-1 text-xs font-bold text-red-700"><AlertTriangle className="h-3 w-3" />Past due</span>}
                    </p>
                    <p className="text-xs text-gray-600">
                      {inv.billing_period_start ? `${dayLabel(inv.billing_period_start)} – ${dayLabel(inv.billing_period_end)}` : dayLabel(inv.created_at)}
                      {inv.due_date ? ` · due ${dayLabel(inv.due_date)}` : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-black text-gray-900 tabular-nums">{money(inv.total)}</p>
                    <p className="text-xs text-gray-600 tabular-nums">{Number(inv.paid) > 0 ? `${money(inv.paid)} paid` : 'Nothing paid'}</p>
                  </div>
                </div>
                {editing === inv.id ? (
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    <input value={po} onChange={e => setPo(e.target.value)} className="input w-auto" placeholder="PO number (blank to clear)" autoFocus />
                    <button onClick={() => savePo.mutate({ invId: inv.id, value: po.trim() })} disabled={readOnly || savePo.isLoading} className="btn-primary text-sm px-3">Save</button>
                    <button onClick={() => setEditing(null)} className="btn-secondary text-sm px-3">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => { setEditing(inv.id); setPo(inv.po_number || ''); }} disabled={readOnly}
                    className="mt-1 text-xs font-bold text-brand-700 underline disabled:opacity-50 disabled:no-underline">
                    {inv.po_number ? `PO ${inv.po_number} · change` : 'Add PO'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}
